import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { MdClose, MdExpandMore, MdExpandLess } from "react-icons/md";
import {
  notificationModalOpenState,
  notificationListState,
} from "../recoil/notification";
import { useNotification } from "../hooks/useNotification";
import "./NotificationModal.css";

const getItemKey = (notification) => notification.wn_id ?? notification._id;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "오후" : "오전";
  const displayHours = hours % 12 || 12;

  return `${month}. ${day}. ${ampm} ${String(displayHours).padStart(
    2,
    "0"
  )}:${String(minutes).padStart(2, "0")}`;
};

const renderContentWithLinks = (text) => {
  if (!text) return text;

  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, match.index),
      });
    }
    parts.push({
      type: "link",
      content: match[0],
    });
    lastIndex = urlPattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  if (parts.length === 0) {
    return text;
  }

  return parts.map((part, index) => {
    if (part.type === "link") {
      return (
        <a
          key={index}
          href={part.content}
          target="_blank"
          rel="noopener noreferrer"
          className="notification-link"
          onClick={(e) => e.stopPropagation()}
        >
          {part.content}
        </a>
      );
    }
    return <span key={index}>{part.content}</span>;
  });
};

const NotificationCard = ({
  notification,
  expanded,
  onToggleExpand,
  onMarkRead,
}) => {
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measureOverflow = useCallback(() => {
    // 펼친 상태에서는 높이 비교가 의미 없으므로, 접혀 있을 때만 측정
    if (expanded) return;

    const titleEl = titleRef.current;
    const contentEl = contentRef.current;
    const titleOverflow =
      titleEl && titleEl.scrollHeight > titleEl.clientHeight + 1;
    const contentOverflow =
      contentEl && contentEl.scrollHeight > contentEl.clientHeight + 1;

    setIsTruncated(Boolean(titleOverflow || contentOverflow));
  }, [expanded]);

  useLayoutEffect(() => {
    measureOverflow();
  }, [
    measureOverflow,
    notification.title,
    notification.content,
    expanded,
  ]);

  useEffect(() => {
    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
  }, [measureOverflow]);

  const title = notification.title || "";
  const content = notification.content || "";
  // 실제로 잘린 경우만 버튼 표시 (펼친 뒤에는 접기 유지)
  const showToggle = isTruncated || expanded;

  return (
    <li
      className={`notification-card ${
        notification.is_read ? "is-read" : "is-unread"
      } ${expanded ? "is-expanded" : ""}`}
      onClick={() => {
        if (!notification.is_read) {
          onMarkRead(notification);
        }
      }}
    >
      <div className="notification-card-meta">
        <time dateTime={notification.date}>{formatDate(notification.date)}</time>
        {!notification.is_read && (
          <span className="notification-unread-dot" aria-label="읽지 않음" />
        )}
      </div>

      <h4
        ref={titleRef}
        className={`notification-card-title ${expanded ? "is-full" : ""}`}
      >
        {title}
      </h4>

      <div
        ref={contentRef}
        className={`notification-card-content ${
          expanded ? "is-full" : "is-clamp"
        }`}
      >
        {renderContentWithLinks(content)}
      </div>

      {showToggle && (
        <button
          type="button"
          className="notification-expand"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(notification);
          }}
        >
          {expanded ? (
            <>
              접기 <MdExpandLess size={18} />
            </>
          ) : (
            <>
              전체 보기 <MdExpandMore size={18} />
            </>
          )}
        </button>
      )}
    </li>
  );
};

const NotificationModal = () => {
  const [isOpen, setIsOpen] = useRecoilState(notificationModalOpenState);
  const notificationList = useRecoilValue(notificationListState);
  const { fetchNotifications, markAsRead } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) {
      setExpandedIds(new Set());
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNotificationClick = async (notification) => {
    if (notification.is_read) {
      return;
    }
    await markAsRead(notification.wn_id);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedIds(new Set());
    fetchNotifications(page);
  };

  const toggleExpand = (notification) => {
    const id = getItemKey(notification);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (!notification.is_read) {
      handleNotificationClick(notification);
    }
  };

  const totalPages = Math.ceil(
    (notificationList.total || 0) / (notificationList.pageSize || 1)
  );

  if (!isOpen) return null;

  return (
    <div
      className="notification-popup-overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="notification-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="notification-popup-header">
          <div className="notification-popup-header-text">
            <h3 id="notification-popup-title">알림 내역</h3>
            <p className="notification-popup-header-sub">
              제목·내용은 펼쳐서 전체 확인
            </p>
          </div>
          <button
            type="button"
            className="notification-popup-close"
            onClick={handleClose}
            aria-label="닫기"
          >
            <MdClose size={20} />
          </button>
        </header>

        <div className="notification-popup-content">
          {notificationList.loading ? (
            <div className="notification-loading">
              <div className="notification-loading-spinner"></div>
              <span>로딩 중...</span>
            </div>
          ) : notificationList.list.length === 0 ? (
            <div className="notification-empty">
              <div className="notification-empty-icon">🔔</div>
              <span>새로운 알림이 없습니다.</span>
            </div>
          ) : (
            <ul className="notification-list">
              {notificationList.list.map((notification) => {
                const itemKey = getItemKey(notification);
                return (
                  <NotificationCard
                    key={itemKey}
                    notification={notification}
                    expanded={expandedIds.has(itemKey)}
                    onToggleExpand={toggleExpand}
                    onMarkRead={handleNotificationClick}
                  />
                );
              })}
            </ul>
          )}
        </div>

        {!notificationList.loading && notificationList.list.length > 0 && (
          <footer className="notification-pagination">
            <div className="notification-pagination-info">
              총 {notificationList.total}개 알림
            </div>
            <div
              className="notification-pagination-controls"
              role="navigation"
              aria-label="페이지"
            >
              <button
                type="button"
                className="notification-pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    className={`notification-pagination-btn ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
              <button
                type="button"
                className="notification-pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                &gt;
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
